<template>
    <fieldset class="mb-3">
        <legend class="visually-hidden">
            {{ label }}
        </legend>
        <div>
            <pc-label
                v-bind:label="label"
                v-bind:show-required="required && !useSubLabel"
                v-bind:show-help="showHelp"
                v-bind:help-link="showHelpLinkOnly"
                v-on:help-clicked="$emit('help-clicked')" />
            <div>
                <slot name="help" />
            </div>
            <div
                v-if="allowSameAs"
                v-bind:class="{ 'mb-3': !useSubLabel }"
                class="row gx-3">
                <pc-select
                    v-bind:class="responsiveHelpers.standard"
                    v-bind:model-value="sameAsValue"
                    v-bind:options="sameAsOptions"
                    v-bind:disabled="disableSameAsDropDown"
                    v-bind:alt-label="sameAsDropdownAltLabel"
                    data-test-role="same-as-choice"
                    value-prop="PublicId"
                    text-prop="Label"
                    v-on:update:model-value="$emit('same-as-selected', $event)">
                    <option value="">
                        {{ newContactOptionLabel }}
                    </option>
                </pc-select>
            </div>
        </div>

        <slot name="beforeAddress" />

        <!-- Address -->
        <div class="row gx-3">
            <pc-input-group v-bind:class="responsiveHelpers.long">
                <address-input-select
                    v-bind:id="groupId"
                    v-bind:model="model"
                    v-bind:group-id="element.DataFieldGroupId"
                    v-bind:validation-group-id="groupId"
                    v-bind:label="useSubLabel ? addressSubLabel : null"
                    v-bind:required="required"
                    v-bind:readonly="readonly"
                    v-bind:validator="validator.line1"
                    v-bind:test-role="testRole.line1"
                    v-bind:use-autocomplete="useAutocomplete"
                    v-bind:use-location-filter="useLocationFilter"
                    v-bind:include-county="includeCounty"
                    v-on:update="$emit('update', model.line1Id, $event)"
                    v-on:validated="$emit('validated', 'line1', $event)"
                    v-on:autofill="onAutofill"
                    v-on:autofill-county="onAutofillCounty" />
            </pc-input-group>
        </div>
        <div class="row gx-3">
            <pc-input-group v-bind:class="responsiveHelpers.long">
                <pc-input
                    v-bind:alt-label="addressLineTwoAltLabel"
                    v-bind:show-validation-errors="false"
                    v-bind:model-value="model.line2"
                    v-bind:group-id="element.DataFieldGroupId"
                    v-bind:validation-group-id="groupId"
                    v-bind:autocomplete="BROWSER_AUTOCOMPLETE_TOKEN.addressLine2"
                    v-bind:readonly="readonly"
                    v-bind:data-test-role="testRole.line2"
                    v-on:update:model-value="$emit('update', model.line2Id, $event)" />
            </pc-input-group>
        </div>
        <div class="row gx-3">
            <pc-input-group v-bind:class="responsiveClasses.cityInput">
                <pc-input
                    ref="city"
                    v-bind:model-value="model.city"
                    v-bind:group-id="element.DataFieldGroupId"
                    v-bind:validation-group-id="groupId"
                    v-bind:autocomplete="BROWSER_AUTOCOMPLETE_TOKEN.addressLevel2"
                    v-bind:validator="validator.city"
                    v-bind:readonly="readonly"
                    v-bind:show-validation-errors="false"
                    v-bind:placeholder="cityPlaceholder"
                    v-bind:data-test-role="testRole.city"
                    v-on:update:model-value="$emit('update', model.cityId, $event)"
                    v-on:validated="$emit('validated', 'city', $event)" />
            </pc-input-group>
            <pc-input-group v-bind:class="responsiveClasses.stateSelect">
                <pc-select
                    ref="state"
                    v-bind:alt-label="stateAltLabel"
                    v-bind:model-value="model.state"
                    v-bind:group-id="element.DataFieldGroupId"
                    v-bind:validation-group-id="groupId"
                    v-bind:autocomplete="BROWSER_AUTOCOMPLETE_TOKEN.addressLevel1"
                    v-bind:validator="validator.state"
                    v-bind:readonly="readonly"
                    v-bind:show-validation-errors="false"
                    v-bind:data-test-role="testRole.state"
                    v-on:update:model-value="$emit('update', model.stateId, $event)"
                    v-on:validated="$emit('validated', 'state', $event)">
                    <option value="" />
                    <optgroup v-bind:label="unitedStatesGroupLabel">
                        <option
                            v-for="state in model.usStates"
                            v-bind:key="state.ShortName"
                            v-bind:value="state.ShortName">
                            {{ state.ShortName }}
                        </option>
                    </optgroup>
                    <optgroup v-bind:label="canadaGroupLabel">
                        <option
                            v-for="state in model.caProvinces"
                            v-bind:key="state.ShortName"
                            v-bind:value="state.ShortName">
                            {{ state.ShortName }}
                        </option>
                    </optgroup>
                </pc-select>
            </pc-input-group>
            <pc-input-group v-bind:class="responsiveClasses.zipInput">
                <pc-input
                    ref="zipcode"
                    v-bind:model-value="model.zipcode"
                    v-bind:group-id="element.DataFieldGroupId"
                    v-bind:validation-group-id="groupId"
                    v-bind:autocomplete="BROWSER_AUTOCOMPLETE_TOKEN.postalCode"
                    v-bind:validator="validator.zipcode"
                    v-bind:readonly="readonly"
                    v-bind:show-validation-errors="false"
                    v-bind:placeholder="zipCodePlaceholder"
                    v-bind:data-test-role="testRole.zip"
                    lazy
                    v-on:update:model-value="$emit('update', model.zipcodeId, $event)"
                    v-on:validated="$emit('validated', 'zipcode', $event)" />
            </pc-input-group>
        </div>
        <pc-transition transition="slide">
            <div
                v-if="model.showCounty"
                class="row gx-3">
                <pc-input-group v-bind:class="responsiveClasses.countySelect">
                    <pc-select
                        ref="county"
                        v-bind:readonly="readonly"
                        v-bind:required="required"
                        v-bind:is-sub-label="true"
                        v-bind:model-value="model.county"
                        v-bind:group-id="element.DataFieldGroupId"
                        v-bind:validation-group-id="groupId"
                        v-bind:validator="validator.county"
                        v-bind:show-validation-errors="false"
                        v-bind:options="model.countyOptions"
                        value-prop="CountyName"
                        text-prop="CountyName"
                        v-bind:label="countySubLabel"
                        v-on:update:model-value="$emit('update', model.countyId, $event)"
                        v-on:validated="$emit('validated', 'county', $event)">
                        <option value="">
                            {{ selectPrompt }}
                        </option>
                    </pc-select>
                </pc-input-group>
            </div>
        </pc-transition>
        <div class="row gx-3">
            <validation-render-helper
                v-bind:validation-group-id="groupId"
                v-bind:validation-error="addressValidationErrors" />
        </div>
        <slot name="afterAddress" />
        <div v-if="hasLocationSubElementDataField">
            <location-sub-element-layout
                v-bind="subElementLayoutModel"
                ref="LocationSubElementLayout"
                v-on:update="sendLocationDataChange"
                v-on:map-initialize-failed="onInitializeFailed"
                v-on:reinitialize-map="reinitializeMap"
                v-on:accept-manual-override="acceptManualOverride"
                v-on:cancel-manual-override="cancelManualOverride"
                v-on:open-manual-override="openManualOverride"
                v-on:longitude-wrapper="longitudeSetter"
                v-on:latitude-wrapper="latitudeSetter"
                v-on:override-longitude-wrapper="overrideLongitudeSetter"
                v-on:override-latitude-wrapper="overrideLatitudeSetter"
                v-on:overlay-geocode-from-dependency="overlayGeocodeFromDependency"
                v-on:geocode-from-dependency="geocodeFromDependency"
                v-on:validated="onLocationValidated"
                v-on:update-has-location-validation-errors="updateHasLocationValidationErrors"
                v-on:help-clicked="$emit('help-clicked')">
            </location-sub-element-layout>
        </div>
    </fieldset>
</template>

<script>
    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./i18n/addressSubElementLayout.i18n";
    import { useStore } from "vuex";
    import validationRenderHelper from "Validation/_validation-error-helper.vue";
    import AddressInputSelect from "./address-input-select.vue";
    import { useLocationShared } from "./locationSubElement";
    import { getResponsiveClassMap } from "Responsive/manager";
    import { ELEMENT_PROPERTY_NAME } from "Types/elementTypes";
    import { BROWSER_AUTOCOMPLETE_TOKEN } from "Util/browserAutocompleteConstants";
    import LocationSubElementLayout from "./locationSubElementLayout.vue";

    const responsiveConfig = {
        cityInput: {
            // col-sm-6 col-lg-4
            col: { sm: 6, lg: 4 },
            always: "col"
        },
        stateSelect: {
            // col-4 col-sm-2 col-lg-1
            col: { xs: 4, sm: 2, lg: 1 }
        },
        zipInput: {
            // col-8 col-sm-2 col-lg-1
            col: { xs: 8, sm: 2, lg: 1 }
        },
        countySelect: {
            // col-sm-6 col-lg-4
            col: { sm: 6, lg: 4 }
        }
    };

    export default {
        components: {
            validationRenderHelper,
            AddressInputSelect,
            LocationSubElementLayout
        },
        inject: {
            viewport: {
                from: "viewport",
                default: null
            }
        },
        props: {
            element: { type: Object, required: true },
            useSubLabel: { type: Boolean, required: true },
            label: { type: String, required: true },
            required: { type: Boolean, required: true },
            showHelp: { type: Boolean, required: true },
            showHelpLinkOnly: { type: String, required: false, default: null },
            readonly: { type: Boolean, required: true },
            isInFormEditor: { type: Boolean, required: true },
            model: { type: Object, required: true },
            responsiveHelpers: { type: Object, required: true },
            allowSameAs: { type: Boolean, required: true },
            sameAsValue: { type: String, required: true },
            sameAsOptions: { type: Array, required: true },
            disableSameAsDropDown: { type: Boolean, required: true },
            validator: { type: Object, required: true },
            addressValidationErrors: { type: Array, required: false, default: null },
            testRole: { type: Object, required: true },
            hasLocationSubElementDataField: { type: Boolean, required: true },
            useAutocomplete: { type: Boolean, required: false, default: false },
            useLocationFilter: { type: Boolean, required: false, default: false },
            includeCounty: { type: Boolean, required: false, default: false }
        },
        emits: ["help-clicked", "same-as-selected", "update", "validated", "update-has-location-validation-errors"],
        setup(props) {
            // Set up location sub element if datafield is present
            if (ELEMENT_PROPERTY_NAME.LocationSubElementId in props.element.Properties || props.isInFormEditor) {
                return {
                    ...useLocationShared(
                        useStore(),
                        props.element,
                        props.element.Properties[ELEMENT_PROPERTY_NAME.LocationSubElementId],
                        props.isInFormEditor
                    )
                };
            }
        },
        data() {
            return {
                BROWSER_AUTOCOMPLETE_TOKEN,
                hasLocationValidationErrors: false
            };
        },
        computed: {
            responsiveClasses() {
                return getResponsiveClassMap(this.viewport, responsiveConfig);
            },
            subElementLayoutModel() {
                // Don't show help on sub-element header since it would duplicate parent element's help message
                return {
                    ...this.locationDataModel,
                    showLabel: false,
                    required: this.required,
                    showHelp: false,
                    showHelpLinkOnly: this.showHelpLinkOnly,
                    readonly: this.readonly,
                    isInFormEditor: this.isInFormEditor,
                    element: this.element
                };
            },
            ...mapI18n(KEYS, NAMESPACE),
            groupId() {
                return this.element.PublicId + "Address";
            }
        },
        watch: {
            hasLocationValidationErrors() {
                this.$emit("update-has-location-validation-errors", this.hasLocationValidationErrors);
            }
        },
        created() {
            if (ELEMENT_PROPERTY_NAME.LocationSubElementId in this.element.Properties && !this.isInFormEditor) {
                this.initializeMap();
                this.$watch("model.line1", () => {
                    this.DependencyChange();
                });
                this.$watch("model.line2", () => {
                    this.DependencyChange();
                });
                this.$watch("model.city", () => {
                    this.DependencyChange();
                });
                this.$watch("model.state", () => {
                    this.DependencyChange();
                });
                this.$watch("model.zipcode", () => {
                    this.DependencyChange();
                });
            }
        },
        methods: {
            async onAutofill(address) {
                this.$emit("update", this.model.line1Id, address.Street);
                this.$emit("update", this.model.line2Id, address.Secondary);
                this.$emit("update", this.model.cityId, address.City);
                this.$emit("update", this.model.stateId, address.State);
                this.$emit("update", this.model.zipcodeId, address.ZipCode);

                const isSecondaryExpansion = !address.Secondary && !address.City && !address.State && !address.ZipCode;
                if (!isSecondaryExpansion) {
                    // ensure validating after update values via autofill
                    await this.$nextTick();
                    this.$refs.city?.validate();
                    this.$refs.state?.validate();
                    this.$refs.zipcode?.validate();
                }
            },
            async onAutofillCounty(county) {
                // only autofill if the provided county is available in the county dropdown
                if (this.model.countyOptions.some((co) => co.CountyName === county)) {
                    this.$emit("update", this.model.countyId, county);
                    // ensure validating after update value via autofill
                    await this.$nextTick();
                    this.$refs.county?.validate();
                }
            },
            async initializeMap() {
                // initializeMapConfig is implemented in locationSubElement.js
                await this.initializeMapConfig();
                if (this.$refs && this.$refs.LocationSubElementLayout) {
                    this.$refs.LocationSubElementLayout.initializeMapControl(this.locationDataModel.mapConfig);
                }
            },
            async reinitializeMap() {
                // reinitializeMapConfig is implemented in locationSubElement.js
                await this.reinitializeMapConfig();
                if (this.$refs && this.$refs.LocationSubElementLayout) {
                    this.$refs.LocationSubElementLayout.reinitializeMapControl();
                    await this.$nextTick();
                    this.$refs.LocationSubElementLayout.reSizeMapControl();
                }
            },
            updateHasLocationValidationErrors(value) {
                this.hasLocationValidationErrors = value;
                this.$emit("update-has-location-validation-errors", value);
            }
        }
    };
</script>
